import { API_BASE_URL, ENDPOINTS } from "../config/api";
import { authFetch, safeJson } from "../utils/auth";
import {
  ApiError,
  type Lead,
  type LeadCheckoutBody,
  type LeadCheckoutResponse,
  type LeadVerifyResponse,
  type MyRequestPayload,
} from "../types/lead";

/**
 * Shared request helper for the curated-lead flow.
 *
 * The backend replies with HTTP 200 for both success and refused calls:
 *   success → { status: 1, data: {...} }
 *   refused → { status: 0, code: "CONFLICT", msg: "...", data: null }
 *
 * This helper unwraps `.data` on success and throws ApiError on refusal
 * so callers can treat it like a normal async/await flow.
 */
async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await authFetch(url, options, 15000);
  } catch (e: any) {
    const reason = e?.name === "AbortError" ? "timeout" : "network";
    throw new ApiError(
      reason === "timeout"
        ? "The server took too long to respond."
        : "Could not reach the server.",
      "NETWORK",
    );
  }

  const json = await safeJson(res);
  if (!json) {
    throw new ApiError("Empty or invalid response from server.", "PARSE");
  }

  // Envelope: { status, data } | { status, code, msg }
  if (typeof json.status === "number") {
    if (json.status === 0) {
      throw new ApiError(
        json.msg || json.message || "Request refused.",
        json.code || "REFUSED",
      );
    }
    return (json.data ?? json) as T;
  }

  // Legacy: raw body or { data: X }
  if (!res.ok) {
    throw new ApiError(
      json.message || json.msg || `Request failed (${res.status}).`,
      `HTTP_${res.status}`,
    );
  }
  return (json.data !== undefined ? json.data : json) as T;
}

/* ── Endpoints ─────────────────────────────────────────────────────── */

/** GET /admin-match-requests/mine */
export function getMyRequest(): Promise<MyRequestPayload> {
  return request<MyRequestPayload>(
    `${API_BASE_URL}${ENDPOINTS.MATCH_REQUEST_MINE}`,
  );
}

/** GET /admin-match-requests/leads/:id */
export function getLead(outreachId: string): Promise<Lead> {
  return request<Lead>(`${API_BASE_URL}${ENDPOINTS.LEADS_BASE}/${outreachId}`);
}

/** POST /admin-match-requests/leads/:id/accept */
export function acceptLead(outreachId: string): Promise<{
  id: string;
  status: "AWAITING_PAYMENT";
  price: number;
  currency: string;
  paymentDueAt: string;
}> {
  return request(
    `${API_BASE_URL}${ENDPOINTS.LEADS_BASE}/${outreachId}/accept`,
    { method: "POST" },
  );
}

/** POST /admin-match-requests/leads/:id/decline */
export function declineLead(
  outreachId: string,
  reason?: string,
): Promise<{ id: string; status: "LEAD_DECLINED"; leadsRemaining: number }> {
  return request(
    `${API_BASE_URL}${ENDPOINTS.LEADS_BASE}/${outreachId}/decline`,
    {
      method: "POST",
      body: JSON.stringify(reason ? { reason } : {}),
    },
  );
}

/** POST /admin-match-requests/leads/:id/checkout */
export function checkoutLead(
  outreachId: string,
  body: LeadCheckoutBody,
): Promise<LeadCheckoutResponse> {
  return request<LeadCheckoutResponse>(
    `${API_BASE_URL}${ENDPOINTS.LEADS_BASE}/${outreachId}/checkout`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

/** POST /admin-match-requests/leads/verify */
export function verifyLeadPayment(
  reference: string,
): Promise<LeadVerifyResponse> {
  return request<LeadVerifyResponse>(
    `${API_BASE_URL}${ENDPOINTS.LEADS_BASE}/verify`,
    {
      method: "POST",
      body: JSON.stringify({ reference }),
    },
  );
}

/* ── Request submission (mobile MD: POST /admin-match-requests) ───── */

export interface CreateRequestBody {
  /** Gender of the match wanted. Optional; falls back to profile.lookingFor. */
  gender?: "male" | "female";
  minAge?: number;
  maxAge?: number;
  city?: string;
  datingIntent?: string;
  interests?: string[];
  ethnicity?: string;
  occupation?: string;
}

export interface CreateRequestResponse {
  id: string;
  status: "PENDING";
  criteria: Record<string, unknown>;
  expiresAt: string;
  createdAt: string;
  leadCapacity: number;
  leadsUsedThisMonth: number;
  leadsRemaining: number;
  capacityResetsAt: string;
}

export function createMatchRequest(
  body: CreateRequestBody,
): Promise<CreateRequestResponse> {
  return request<CreateRequestResponse>(
    `${API_BASE_URL}${ENDPOINTS.MATCH_REQUEST_CREATE}`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}
